import { useEffect, useId, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ApiRequestError } from '@/lib/apiClient';
import { instituteToday } from '@/features/teacher/utils';
import { formatCurrency, formatDate } from '@/lib/format';
import type { SalaryComponent, StaffSalary, StaffSalaryRecord } from '@/types/payroll';
import { payrollApi } from '../api/payroll.api';
import {
  FIRST_SALARY_OPTIONS,
  LATER_SALARY_OPTIONS,
  SALARY_REVISION_LABEL,
} from '../constants';
import { salaryFormSchema, type SalaryFormValues } from '../schemas/payroll.schema';

export interface SalaryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffUuid: string;
  staffName: string;
  /** `YYYY-MM-DD` — a salary cannot start before it. */
  joinedOn: string | null;
  /** Everything already set for this person. */
  record: StaffSalaryRecord;
  /** The institute's pay heads, switched-off ones included. */
  components: SalaryComponent[];
  /**
   * `new` sets a salary from a later date (the first, or an increment).
   * `correct` replaces the latest one — the server keeps the old on record.
   */
  mode: 'new' | 'correct';
  onSaved: (record: StaffSalaryRecord) => void;
}

type FieldErrors = Partial<Record<'effectiveFrom' | 'revisionType' | 'basic' | 'note', string>>;

const KIND_RANK = { EARNING: 0, DEDUCTION: 1 } as const;

/** Rupees to the paisa, rounding half up — the same rule the server prices by. */
function toPaise(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Setting a salary, giving an increment, or correcting the latest salary.
 *
 * Every head the institute uses is listed with a box; a blank box leaves that
 * head off this person's salary. The totals underneath are a preview worked out
 * as the office types — the server prices the salary again from Basic and the
 * values, and its figures are the ones saved.
 */
export function SalaryFormModal({
  isOpen,
  onClose,
  staffUuid,
  staffName,
  joinedOn,
  record,
  components,
  mode,
  onSaved,
}: SalaryFormModalProps) {
  const formId = useId();
  const latest: StaffSalary | null = record.history[0] ?? null;
  /** The salary the one being corrected follows — decides which reasons fit. */
  const hasEarlier = mode === 'correct' ? record.history.length > 1 : latest !== null;

  const revisionOptions = useMemo(() => {
    if (!hasEarlier) {
      // A first salary may be corrected as a correction, but never be an increment.
      return mode === 'correct'
        ? [...FIRST_SALARY_OPTIONS, { value: 'CORRECTION' as const, label: SALARY_REVISION_LABEL.CORRECTION }]
        : FIRST_SALARY_OPTIONS;
    }
    return LATER_SALARY_OPTIONS;
  }, [hasEarlier, mode]);

  /**
   * The heads offered: every one in use, plus — when correcting — any switched-off
   * head already on that salary, which it may keep.
   */
  const offered = useMemo(() => {
    const onSalary = new Set(mode === 'correct' && latest ? latest.lines.map((line) => line.componentUuid) : []);
    return components
      .filter((head) => head.isActive || onSalary.has(head.uuid))
      // Earnings before deductions, as a payslip reads.
      .sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind] || a.sortOrder - b.sortOrder);
  }, [components, latest, mode]);

  const [values, setValues] = useState<SalaryFormValues>({
    effectiveFrom: '',
    revisionType: 'JOINING',
    basic: '',
    lines: [],
    note: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // A new salary starts from the latest one's figures — an increment is
    // usually Basic moved and the rest kept — or, the first time, from each
    // head's default. A correction starts from exactly what it corrects.
    const source = latest;
    const valueFor = (head: SalaryComponent) => {
      const line = source?.lines.find((entry) => entry.componentUuid === head.uuid);
      if (line) return line.value;
      return source ? '' : (head.defaultValue ?? '');
    };

    setValues({
      effectiveFrom:
        mode === 'correct' && latest
          ? latest.effectiveFrom
          : latest
            ? ''
            : (joinedOn ?? instituteToday()),
      revisionType:
        mode === 'correct' && latest ? latest.revisionType : latest ? 'INCREMENT' : 'JOINING',
      basic: source ? String(Number(source.basic)) : '',
      lines: offered.map((head) => ({
        componentUuid: head.uuid,
        calculation: head.calculation,
        value: valueFor(head) === '' ? '' : String(Number(valueFor(head))),
      })),
      note: mode === 'correct' && latest ? (latest.note ?? '') : '',
    });
    setFieldErrors({});
    setLineErrors({});
    setFormError(null);
  }, [isOpen, mode, latest, offered, joinedOn]);

  function set<K extends keyof SalaryFormValues>(key: K, value: SalaryFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function setLine(componentUuid: string, value: string) {
    setValues((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.componentUuid === componentUuid ? { ...line, value } : line)),
    }));
  }

  // --- the live preview --------------------------------------------------
  const preview = useMemo(() => {
    const basic = Number(values.basic) || 0;
    let earnings = 0;
    let deductions = 0;
    const amounts = new Map<string, number>();

    for (const line of values.lines) {
      const head = offered.find((entry) => entry.uuid === line.componentUuid);
      const value = Number(line.value);
      if (!head || line.value === '' || Number.isNaN(value)) continue;
      const amount = head.calculation === 'PERCENT_OF_BASIC' ? toPaise((basic * value) / 100) : value;
      amounts.set(head.uuid, amount);
      if (head.kind === 'EARNING') earnings += amount;
      else deductions += amount;
    }

    const gross = toPaise(basic + earnings);
    return { amounts, gross, deductions: toPaise(deductions), net: toPaise(gross - deductions) };
  }, [values.basic, values.lines, offered]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = salaryFormSchema.safeParse(values);

    if (!parsed.success) {
      const errors: FieldErrors = {};
      const perLine: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const [field, index] = issue.path;
        if (field === 'lines' && typeof index === 'number') {
          const uuid = values.lines[index]?.componentUuid;
          if (uuid && !perLine[uuid]) perLine[uuid] = issue.message;
        } else if (typeof field === 'string' && !(field in errors)) {
          errors[field as keyof FieldErrors] = issue.message;
        }
      }
      setFieldErrors(errors);
      setLineErrors(perLine);
      return;
    }

    const percentTooHigh = parsed.data.lines.find(
      (line) => line.calculation === 'PERCENT_OF_BASIC' && line.value !== null && line.value > 100,
    );
    if (percentTooHigh) {
      setLineErrors({ [percentTooHigh.componentUuid]: 'A percentage cannot be more than 100' });
      return;
    }
    if (preview.net < 0) {
      setFormError('The deductions come to more than the gross pay. Check the amounts.');
      return;
    }

    setFieldErrors({});
    setLineErrors({});
    setFormError(null);
    setIsSaving(true);

    const payload = {
      effectiveFrom: parsed.data.effectiveFrom,
      revisionType: parsed.data.revisionType,
      basic: parsed.data.basic,
      // A blank box leaves the head off this salary.
      lines: parsed.data.lines
        .filter((line): line is typeof line & { value: number } => line.value !== null)
        .map((line) => ({ componentUuid: line.componentUuid, value: line.value })),
      note: parsed.data.note,
    };

    try {
      const saved =
        mode === 'correct' && latest
          ? await payrollApi.correctSalary(latest.uuid, payload)
          : await payrollApi.setStaffSalary(staffUuid, payload);
      onSaved(saved);
    } catch (caught) {
      setFormError(
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const title =
    mode === 'correct' ? `Correct ${staffName}'s salary` : latest ? `Revise ${staffName}'s salary` : `Set ${staffName}'s salary`;
  const earliest =
    mode === 'correct'
      ? (record.history[1]?.effectiveFrom ?? joinedOn ?? undefined)
      : (latest?.effectiveFrom ?? joinedOn ?? undefined);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={
        mode === 'correct'
          ? 'Fixes the latest salary in place of a new one. The figures first entered stay on record.'
          : latest
            ? 'A new salary from the date you choose. The current one stays in force until the day before.'
            : 'Basic pay plus each pay head that applies. Leave a head blank to leave it off.'
      }
      size="lg"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" form={formId} isLoading={isSaving} leftIcon={<Save className="h-4 w-4" />}>
            {mode === 'correct' ? 'Save correction' : 'Save salary'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save this salary">
            {formError}
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <DatePicker
            label="Starts from"
            value={values.effectiveFrom}
            min={earliest}
            error={fieldErrors.effectiveFrom}
            hint={
              mode === 'new' && latest
                ? `After ${formatDate(latest.effectiveFrom)}, when the current salary started.`
                : undefined
            }
            onChange={(event) => set('effectiveFrom', event.target.value)}
          />
          <Select
            label="Reason"
            options={revisionOptions}
            value={values.revisionType}
            error={fieldErrors.revisionType}
            onChange={(event) =>
              set('revisionType', event.target.value as SalaryFormValues['revisionType'])
            }
          />
          <Input
            label="Basic pay (₹ a month)"
            inputMode="decimal"
            placeholder="e.g. 30000"
            value={values.basic}
            error={fieldErrors.basic}
            onChange={(event) => set('basic', event.target.value)}
          />
        </div>

        {offered.length === 0 ? (
          <Alert tone="info" title="No pay heads set up yet">
            This salary will be Basic pay alone. Add allowances and deductions on the Pay heads page,
            then revise it.
          </Alert>
        ) : (
          <div className="overflow-hidden rounded-card border border-line">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Pay heads on this salary</caption>
              <thead className="bg-surface-2 text-xs text-ink-2">
                <tr>
                  <th scope="col" className="px-3 py-2 font-[560]">Pay head</th>
                  <th scope="col" className="w-40 px-3 py-2 font-[560]">Value</th>
                  <th scope="col" className="w-32 px-3 py-2 text-right font-[560]">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {offered.map((head) => {
                  const line = values.lines.find((entry) => entry.componentUuid === head.uuid);
                  const amount = preview.amounts.get(head.uuid);
                  const isPercent = head.calculation === 'PERCENT_OF_BASIC';
                  return (
                    <tr key={head.uuid}>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-[560] text-ink">{head.name}</span>
                          <Badge tone={head.kind === 'EARNING' ? 'success' : 'critical'}>
                            {head.kind === 'EARNING' ? 'Earning' : 'Deduction'}
                          </Badge>
                          {!head.isActive && <Badge tone="neutral">Switched off</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          hideLabel
                          label={`${head.name} — ${isPercent ? 'percentage of Basic' : 'amount in rupees'}`}
                          inputMode="decimal"
                          placeholder={isPercent ? '% of Basic' : '₹'}
                          value={line?.value ?? ''}
                          error={lineErrors[head.uuid]}
                          onChange={(event) => setLine(head.uuid, event.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                        {amount === undefined ? '—' : formatCurrency(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* The preview. The server prices it again, and its figures are saved. */}
        <dl className="grid grid-cols-3 gap-2 rounded-card border border-line bg-surface-2 p-3 text-center">
          <div>
            <dt className="lbl">Gross</dt>
            <dd className="text-base font-[600] tabular-nums text-ink">{formatCurrency(preview.gross)}</dd>
          </div>
          <div>
            <dt className="lbl">Deductions</dt>
            <dd className="text-base font-[600] tabular-nums text-crit">{formatCurrency(preview.deductions)}</dd>
          </div>
          <div>
            <dt className="lbl">Net pay</dt>
            <dd className="text-base font-[600] tabular-nums text-ok">{formatCurrency(preview.net)}</dd>
          </div>
        </dl>

        <Input
          label="Note"
          placeholder="Optional — e.g. Annual increment 2026-27"
          value={values.note ?? ''}
          error={fieldErrors.note}
          onChange={(event) => set('note', event.target.value)}
        />
      </form>
    </Modal>
  );
}
